import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { Spin, Table } from "antd";
import queryKeys from "common/queryKey";
import { useScrollFetchPost2 } from "hooks/useScrollFetchPost2";
import { getUserInfo, getUserList } from "redux/selectors";
import { useSelector } from "react-redux";
import { generateNiceWeekFromWeek } from "./useWeekData";
import { APPROVED, DECLINED, DRAFT, ONGOING, PENDING, WITHDRAWN } from ".";
import { sumBy } from "lodash";

const TimeSheetHistory = ({ handleClickTimeSheet }) => {
  const historyColumns = [
    {
      title: "Period",
      dataIndex: "period",
      key: "period",
    },
    {
      title: "Timesheet Submitted Date",
      dataIndex: "timesheetSubmittedDate",
      key: "timesheetSubmittedDate",
    },
    {
      title: "Approved",
      dataIndex: "approved",
      key: "approved",
    },
    {
      title: "Rejected",
      dataIndex: "rejected",
      key: "rejected",
    },
    {
      title: "Pending for Approval",
      dataIndex: "pending_approval",
      key: "pending_approval",
    },
    {
      title: "Pending for Submission",
      dataIndex: "pending_submission",
      key: "pending_submission",
    },
    {
      title: "Total Hours",
      dataIndex: "total_hours",
      key: "total_hours",
    },
    {
      title: "Total Claims",
      dataIndex: "total_claims",
      key: "total_claims",
      render: (value) =>
        value != null ? Number(value)?.toLocaleString("en-US") : "-",
    },
  ];

  const { getAdminApprovalTimesheet } = queryKeys;

  const { key, url } = getAdminApprovalTimesheet("all");

  const userInfo = useSelector(getUserInfo);

  const userList = useSelector(getUserList);

  const currentUser = userList.find((user) => user.userid === userInfo.userid);

  const country = currentUser?.managingoffice?.[0];

  const { data, isLoading: isHistoryLoading } = useScrollFetchPost2(key, {
    endPoint: url,
    queryParam: "LastEvaluatedKey",
    extra: {
      weeks: [],
      // statusArray: ["pending", "approved", "declined", "ongoing", "withdrawn"],
      statusArray: [PENDING, APPROVED, DECLINED, ONGOING, WITHDRAWN, DRAFT],
      employeeId: [userInfo.userid],
      country: country,
    },
  });

  const timesheetsData = useMemo(() => {
    if (!data) return [];
    return data?.pages?.reduce((acc, page) => {
      if (page?.response?.response === undefined) return acc;
      return [...acc, ...page?.response?.response];
    }, []);
  }, [data]);

  const [timeSheets, setTimeSheets] = React.useState([]);

  useEffect(() => {
    if (timesheetsData) {
      const data = timesheetsData
        .sort(
          (a, b) =>
            new Date(b.week.split('-')[1]) - new Date(a.week.split('-')[1])
        )
        .map((timesheet) => {
          const timeSheet = timesheet?.timeSheet || [];

          const totalHours = timeSheet.reduce((acc, section) => {
            return (
              acc +
              (parseFloat(section.mon) || 0) +
              (parseFloat(section.tue) || 0) +
              (parseFloat(section.wed) || 0) +
              (parseFloat(section.thu) || 0) +
              (parseFloat(section.fri) || 0) +
              (parseFloat(section.sat) || 0) +
              (parseFloat(section.sun) || 0)
            );
          }, 0).toFixed(3);

          const totalClaims = timeSheet.reduce((acc, section) => {
            return (
              acc +
              parseFloat(sumBy(section.claims, (claim) => parseInt(claim.amount)))
            );
          }, 0);

          const approved = timeSheet.filter(
            (section) =>
              section.status === "approved" ||
              section.status === "ongoing" ||
              section.client === "ap" ||
              (section.client === "nc" && section.status === "approved") || // Include all approved NC entries
              (section.client === "c" && section.status === "approved") // Include all approved C entries
          );

          const approvedHours = approved.reduce((acc, section) => {
            return (
              acc +
              (parseFloat(section.mon) || 0) +
              (parseFloat(section.tue) || 0) +
              (parseFloat(section.wed) || 0) +
              (parseFloat(section.thu) || 0) +
              (parseFloat(section.fri) || 0) +
              (parseFloat(section.sat) || 0) +
              (parseFloat(section.sun) || 0)
            );
          }, 0).toFixed(3);

          const rejected = timeSheet.filter(
            (section) => section.status === "declined"
          );

          const rejectedHours = rejected.reduce((acc, section) => {
            return (
              acc +
              (parseFloat(section.mon) || 0) +
              (parseFloat(section.tue) || 0) +
              (parseFloat(section.wed) || 0) +
              (parseFloat(section.thu) || 0) +
              (parseFloat(section.fri) || 0) +
              (parseFloat(section.sat) || 0) +
              (parseFloat(section.sun) || 0)
            );
          }, 0).toFixed(3);

          const pending_submission = timeSheet.filter(
            (section) => section?.status === "draft" || section?.status === "withdrawn"
          );

          const draftHours = pending_submission.reduce((acc, section) => {
            return (
              acc +
              (parseFloat(section.mon) || 0) +
              (parseFloat(section.tue) || 0) +
              (parseFloat(section.wed) || 0) +
              (parseFloat(section.thu) || 0) +
              (parseFloat(section.fri) || 0) +
              (parseFloat(section.sat) || 0) +
              (parseFloat(section.sun) || 0)
            );
          }, 0).toFixed(3);

          return {
            period: timesheet?.week
              ? generateNiceWeekFromWeek(timesheet?.week)
              : "-",
            approved: Number(approvedHours),
            rejected: Number(rejectedHours),
            pending_approval: Number(draftHours) > 0 ? 0 : Number(totalHours) - Number(approvedHours) - Number(rejectedHours),
            pending_submission: Number(draftHours),
            total_hours: Number(totalHours),
            total_claims: totalClaims || "0",
            timesheetSubmittedDate: timesheet?.timesheetSubmittedDate || "-"
          };
        });
      setTimeSheets(data);
    }
  }, [timesheetsData]);

  return (
    <HistoryWrap>
      <Spin spinning={isHistoryLoading}>
        <HistoryTable
          columns={historyColumns}
          dataSource={timeSheets}
          bordered={true}
          pagination={false}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) => {
                handleClickTimeSheet(record?.period);
              },
            };
          }}
        />
      </Spin>
    </HistoryWrap>
  );
};

export default TimeSheetHistory;

const HistoryWrap = styled.div`
  .main-back {
    display: flex;
    align-items: center;
    justify-content: start;
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 10px;
    .backicon {
      font-size: 16px;
    }
  }
`;

const HistoryTable = styled(Table)`
  .ant-table-cell.nowrap {
    white-space: nowrap;
  }
  .ant-table-cell.border-right-none {
    border-right: none !important;
  }
  .ant-table-cell.border-bottom-none {
    border-bottom: none;
  }
  .ant-table-cell {
    padding: 0.5rem 0.75rem;
  }
  .ant-table-cell-row-hover {
    cursor: pointer;
    background-color: #efefef;
  }
`;
